import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth, homePathFor, isGuestAccount } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import HubPage from './pages/HubPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import DeadlinesPage from './pages/DeadlinesPage';
import ResearchPage from './pages/ResearchPage';
import SourceDetailPage from './pages/SourceDetailPage';
import SettingsPage from './pages/SettingsPage';
import CollegeVisitsPage from './pages/CollegeVisitsPage';
import StudyPlannerPage from './pages/StudyPlannerPage';
import SatPrepPage from './pages/SatPrepPage';
import RecallPage from './pages/RecallPage';
import CollegeShell from './pages/college/CollegeShell';
import CollegeOverviewPage from './pages/college/CollegeOverviewPage';
import CollegeListPage from './pages/college/CollegeListPage';
import CollegeDetailPage from './pages/college/CollegeDetailPage';
import CollegeProfilePage from './pages/college/CollegeProfilePage';
import CollegeBrowsePage from './pages/college/CollegeBrowsePage';
import CollegeBrowseDetailPage from './pages/college/CollegeBrowseDetailPage';
import CollegeDocsPage from './pages/college/CollegeDocsPage';
import CollegeEssaysPage from './pages/college/CollegeEssaysPage';
import CollegeEssayDetailPage from './pages/college/CollegeEssayDetailPage';
import CollegeAccessPage from './pages/college/CollegeAccessPage';
import SharedDeskShell from './pages/college/SharedDeskShell';
import SharedOverviewPage from './pages/college/SharedOverviewPage';
import SharedCollegeListPage from './pages/college/SharedCollegeListPage';
import SharedEssaysPage from './pages/college/SharedEssaysPage';
import SharedEssayDetailPage from './pages/college/SharedEssayDetailPage';
import SharedDeadlinesPage from './pages/college/SharedDeadlinesPage';
import SharedTasksPage from './pages/college/SharedTasksPage';
import SharedDeskIndexPage from './pages/college/SharedDeskIndexPage';
import InviteAcceptPage from './pages/college/InviteAcceptPage';

// Layout
import AppLayout from './components/layout/AppLayout';

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;

  return children;
};

// Anything belonging to the account holder's own work: their coursework, their
// College Desk, their settings. A guest — somebody invited to follow another
// student's applications — has no such workspace, so they land on the desk
// that was shared with them instead of an empty School app.
const StudentRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  if (isGuestAccount(user)) return <Navigate to={homePathFor(user)} replace />;

  return children;
};

// Sends a signed-in account wherever it belongs.
const HomeRoute = () => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;

  return <Navigate to={homePathFor(user)} replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={homePathFor(user)} replace />;
  }

  return children;
};

// Preserves the id when forwarding a bookmarked /essays/:id to its new home.
function EssayRedirect() {
  const { id } = useParams();
  return <Navigate to={`/college/essays/${id}`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/home" element={<HomeRoute />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />

      <Route path="/hub" element={<StudentRoute><HubPage /></StudentRoute>} />

      <Route path="/college" element={<StudentRoute><CollegeShell /></StudentRoute>}>
        <Route index element={<CollegeOverviewPage />} />
        <Route path="list" element={<CollegeListPage />} />
        <Route path="browse" element={<CollegeBrowsePage />} />
        <Route path="browse/:id" element={<CollegeBrowseDetailPage />} />
        <Route path="docs" element={<CollegeDocsPage />} />
        <Route path="essays" element={<CollegeEssaysPage />} />
        <Route path="essays/:id" element={<CollegeEssayDetailPage />} />
        <Route path="access" element={<CollegeAccessPage />} />
        <Route path="profile" element={<CollegeProfilePage />} />
        <Route path=":id" element={<CollegeDetailPage />} />
      </Route>

      {/* A parent or advisor looking at somebody else's Desk. Its own shell —
          the banner, the tabs and the scope note all belong to that view. */}
      <Route path="/shared" element={<ProtectedRoute><SharedDeskIndexPage /></ProtectedRoute>} />
      <Route path="/shared/:grantId" element={<ProtectedRoute><SharedDeskShell /></ProtectedRoute>}>
        <Route index element={<SharedOverviewPage />} />
        <Route path="list" element={<SharedCollegeListPage />} />
        <Route path="essays" element={<SharedEssaysPage />} />
        <Route path="essays/:essayId" element={<SharedEssayDetailPage />} />
        <Route path="deadlines" element={<SharedDeadlinesPage />} />
        <Route path="tasks" element={<SharedTasksPage />} />
      </Route>
      <Route path="/invite/:token" element={<ProtectedRoute><InviteAcceptPage /></ProtectedRoute>} />

      <Route path="/dashboard" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<DashboardPage />} />
      </Route>
      
      <Route path="/projects" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<ProjectsPage />} />
        <Route path=":id" element={<ProjectDetailPage />} />
      </Route>
      
      {/* Essays moved into The College Desk. Old links keep working. */}
      <Route path="/essays" element={<Navigate to="/college/essays" replace />} />
      <Route path="/essays/:id" element={<EssayRedirect />} />

      <Route path="/deadlines" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<DeadlinesPage />} />
      </Route>
      
      <Route path="/research" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<ResearchPage />} />
        <Route path=":id" element={<SourceDetailPage />} />
      </Route>
      
      <Route path="/college-visits" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<CollegeVisitsPage />} />
      </Route>

      <Route path="/planner" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<StudyPlannerPage />} />
      </Route>

      <Route path="/recall" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<RecallPage />} />
      </Route>

      <Route path="/sat-prep" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<SatPrepPage />} />
      </Route>

      <Route path="/settings" element={<StudentRoute><AppLayout /></StudentRoute>}>
        <Route index element={<SettingsPage />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
